import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2 } from "lucide-react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";

const EMPTY = {
  slug: "",
  name: "",
  subtitle: "",
  description: "",
  prices: [{ _key: "seed-250", weight: "250g", price: "" }, { _key: "seed-500", weight: "500g", price: "" }, { _key: "seed-1kg", weight: "1kg", price: "" }],
  tag: "",
  spiciness: "Medium",
  oil_type: "Organic Cold-Pressed Mustard Oil",
  image_url: "",
  ingredients: "",
  stock: 100,
  active: true,
};

const rowKey = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `k-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);

function pricesToRows(prices) {
  if (!prices) return EMPTY.prices;
  return Object.entries(prices).map(([weight, price]) => ({ _key: rowKey(), weight, price: String(price) }));
}
function rowsToPrices(rows) {
  const out = {};
  rows.forEach((r) => {
    const w = (r.weight || "").trim();
    const p = parseFloat(r.price);
    if (w && !isNaN(p) && p > 0) out[w] = p;
  });
  return out;
}

export default function ProductForm({ open, onOpenChange, product, onSaved }) {
  const isEdit = !!product;
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (open) {
      if (product) {
        setForm({
          slug: product.slug || "",
          name: product.name || "",
          subtitle: product.subtitle || "",
          description: product.description || "",
          prices: pricesToRows(product.prices),
          tag: product.tag || "",
          spiciness: product.spiciness || "",
          oil_type: product.oil_type || "",
          image_url: product.image_url || "",
          ingredients: (product.ingredients || []).join(", "),
          stock: product.stock ?? 100,
          active: product.active ?? true,
        });
      } else {
        setForm(EMPTY);
      }
    }
  }, [open, product]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const addPriceRow = () => setForm((f) => ({ ...f, prices: [...f.prices, { _key: rowKey(), weight: "", price: "" }] }));
  const removePriceRow = (idx) => setForm((f) => ({ ...f, prices: f.prices.filter((_, i) => i !== idx) }));
  const setPriceRow = (idx, key, val) => setForm((f) => ({
    ...f,
    prices: f.prices.map((r, i) => i === idx ? { ...r, [key]: val } : r),
  }));

  const uploadImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("da_admin_token");
      const res = await fetch(`${API}/admin/upload-image`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      const absUrl = data.url.startsWith("http") ? data.url : `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      setForm((f) => ({ ...f, image_url: absUrl }));
      toast.success("Image uploaded");
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const validate = () => {
    if (!form.slug.trim()) return "Slug required (e.g. aam-ka-achaar)";
    if (!/^[a-z0-9-]+$/.test(form.slug.trim())) return "Slug must be lowercase letters, digits, hyphens only";
    if (!form.name.trim()) return "Product name required";
    if (!form.subtitle.trim()) return "Subtitle required";
    if (!form.description.trim()) return "Description required";
    if (!form.image_url.trim()) return "Image URL or upload required";
    const priceObj = rowsToPrices(form.prices);
    if (Object.keys(priceObj).length === 0) return "At least one weight+price row required";
    return null;
  };

  const save = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      const priceObj = rowsToPrices(form.prices);
      const payload = {
        slug: form.slug.trim(),
        name: form.name.trim(),
        subtitle: form.subtitle.trim(),
        description: form.description.trim(),
        prices: priceObj,
        tag: form.tag.trim() || null,
        spiciness: form.spiciness.trim() || null,
        oil_type: form.oil_type.trim() || null,
        image_url: form.image_url.trim(),
        ingredients: form.ingredients
          ? form.ingredients.split(",").map((s) => s.trim()).filter(Boolean)
          : [],
        stock: parseInt(form.stock, 10) || 0,
        active: !!form.active,
      };
      if (isEdit) {
        await api.patch(`/admin/products/${product.id}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/admin/products", payload);
        toast.success("Product added");
      }
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl bg-white max-h-[90vh] overflow-y-auto" data-testid="product-form-modal">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{isEdit ? `Edit: ${product?.name}` : "Add New Product"}</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label>Slug (URL id) *</Label>
            <Input data-testid="pf-slug" value={form.slug} onChange={update("slug")} disabled={isEdit} placeholder="aam-ka-achaar" className="mt-1.5" />
            <p className="text-xs text-[#6E675F] mt-1">Cannot be changed after creation</p>
          </div>
          <div>
            <Label>Product name *</Label>
            <Input data-testid="pf-name" value={form.name} onChange={update("name")} placeholder="Aam ka Achaar" className="mt-1.5" />
          </div>
          <div className="sm:col-span-2">
            <Label>Subtitle *</Label>
            <Input data-testid="pf-subtitle" value={form.subtitle} onChange={update("subtitle")} placeholder="Traditional Raw Mango Pickle" className="mt-1.5" />
          </div>
          <div className="sm:col-span-2">
            <Label>Description *</Label>
            <Textarea data-testid="pf-description" value={form.description} onChange={update("description")} rows={3} className="mt-1.5" />
          </div>
          <div>
            <Label>Tag</Label>
            <Input data-testid="pf-tag" value={form.tag} onChange={update("tag")} placeholder="Bestseller" className="mt-1.5" />
          </div>
          <div>
            <Label>Spiciness</Label>
            <Input data-testid="pf-spiciness" value={form.spiciness} onChange={update("spiciness")} placeholder="Medium Hot" className="mt-1.5" />
          </div>
          <div className="sm:col-span-2">
            <Label>Oil type</Label>
            <Input data-testid="pf-oil" value={form.oil_type} onChange={update("oil_type")} className="mt-1.5" />
          </div>
          <div className="sm:col-span-2">
            <Label>Ingredients (comma separated)</Label>
            <Textarea data-testid="pf-ingredients" value={form.ingredients} onChange={update("ingredients")} rows={2} placeholder="Raw mango, Cold-pressed mustard oil, Fennel, ..." className="mt-1.5" />
          </div>
          <div className="sm:col-span-2">
            <Label>Product image *</Label>
            {form.image_url ? (
              <div className="mt-2 flex items-center gap-3">
                <img src={form.image_url} alt="Preview" className="w-20 h-20 rounded-lg object-cover" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-[#6E675F] truncate">{form.image_url}</p>
                  <div className="flex gap-2 mt-1">
                    <label className="text-xs text-[#9E3B21] font-semibold cursor-pointer hover:underline">
                      Replace
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files?.[0])} data-testid="pf-image-replace" />
                    </label>
                    <button type="button" className="text-xs text-[#6E675F] hover:underline" onClick={() => setForm((f) => ({ ...f, image_url: "" }))}>Remove</button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="mt-1.5 flex items-center gap-2">
                <label className="flex-1 cursor-pointer">
                  <span className="block text-xs bg-[#F4EFE6] border-2 border-dashed border-[#E6DEC3] rounded-lg p-4 text-center hover:border-[#9E3B21] transition-colors">
                    {uploading ? "Uploading…" : "Click to upload image (JPG/PNG, up to 6MB)"}
                  </span>
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files?.[0])} data-testid="pf-image-upload" />
                </label>
              </div>
            )}
            <Input value={form.image_url} onChange={update("image_url")} placeholder="or paste an image URL" data-testid="pf-image-url" className="mt-2 text-xs" />
          </div>
          <div className="sm:col-span-2">
            <Label>Weights & Prices *</Label>
            <p className="text-xs text-[#6E675F] mt-0.5 mb-2">Use e.g. 250g, 500g, 1kg. Add as many rows as you like.</p>
            <div className="space-y-2" data-testid="pf-price-rows">
              {form.prices.map((row, idx) => (
                <div key={row._key || `row-${idx}`} className="flex gap-2 items-center">
                  <Input
                    value={row.weight}
                    onChange={(e) => setPriceRow(idx, "weight", e.target.value)}
                    placeholder="e.g. 500g"
                    data-testid={`pf-price-weight-${idx}`}
                    className="flex-1"
                  />
                  <Input
                    value={row.price}
                    onChange={(e) => setPriceRow(idx, "price", e.target.value)}
                    placeholder="Price ₹"
                    type="number"
                    data-testid={`pf-price-value-${idx}`}
                    className="w-32"
                  />
                  <button
                    type="button"
                    onClick={() => removePriceRow(idx)}
                    className="p-2 text-[#9E3B21] hover:bg-[#9E3B21]/10 rounded-lg"
                    data-testid={`pf-price-remove-${idx}`}
                    aria-label="Remove weight"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            <Button type="button" variant="outline" size="sm" onClick={addPriceRow} data-testid="pf-price-add" className="mt-2 rounded-full">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add weight
            </Button>
          </div>
          <div>
            <Label>Stock</Label>
            <Input data-testid="pf-stock" type="number" value={form.stock} onChange={update("stock")} className="mt-1.5" />
          </div>
          <div className="flex items-end">
            <div className="flex items-center gap-3">
              <Switch data-testid="pf-active" checked={form.active} onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))} />
              <div>
                <Label className="cursor-pointer">Active</Label>
                <p className="text-xs text-[#6E675F]">Show on public site</p>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="pf-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} className="btn-primary" data-testid="pf-save">
            {saving ? "Saving..." : isEdit ? "Save changes" : "Add product"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
